// Track all active scroll locks
const locks = new Set();

// Store the scroll position before locking
let lastScrollY = 0;

// Initialise css variables
document.addEventListener('DOMContentLoaded', () => {
  resetBodyScrollVariables();
});

/**
 * Resets the css variables associated with body scroll lock
 */
function resetBodyScrollVariables() {
  document.documentElement.style.setProperty('--locked-body-scroll-top', '0px');
  document.documentElement.style.setProperty('--locked-body-scrollbar-gap', '0px');
}

/**
 * Locks body scroll
 * Multiple locks can be active simultaneously. The body will only unlock when all locks are released.
 *
 * @param targetElement - The element associated with this lock (e.g., menu, modal)
 * @returns Cleanup function to unlock this specific lock
 */
export function lockBodyScroll(targetElement) {
  // Prevent duplicate locks for the same element
  if (locks.has(targetElement)) {
    return () => unlockBodyScroll(targetElement);
  }

  locks.add(targetElement);

  // Apply body lock only on first lock
  if (locks.size === 1) {
    // Save current scroll position
    lastScrollY = window.scrollY;
    document.documentElement.style.setProperty('--locked-body-scroll-top', `${lastScrollY}px`);

    // Calculate scrollbar width and compensate to prevent layout shift
    const scrollBarGap = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--locked-body-scrollbar-gap', `${scrollBarGap}px`);
    if (scrollBarGap > 0) document.body.style.paddingRight = 'var(--locked-body-scrollbar-gap)';

    // Lock body scroll using position:fixed technique
    document.body.style.position = 'fixed';
    document.body.style.top = 'calc(var(--locked-body-scroll-top) * -1)';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'contain';
  }

  return () => unlockBodyScroll(targetElement);
}

/**
 * Unlocks body scroll for a specific target element.
 *
 * @param targetElement - The element whose body scroll lock should be removed
 */
export function unlockBodyScroll(targetElement) {
  if (!locks.has(targetElement)) {
    return;
  }

  locks.delete(targetElement);

  // Only restore body scroll when all locks are removed
  if (locks.size === 0) {
    // Remove all style overrides
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overscroll-behavior');

    // Restore scroll position
    window.scrollTo({ top: lastScrollY, behavior: 'instant' });
    lastScrollY = 0;

    resetBodyScrollVariables();
  }
}

/**
 * Unlocks all active body scroll locks at once.
 *
 * Useful for cleanup or kill-switch unlock scenarios.
 */
export function unlockAllBodyScroll() {
  [...locks].forEach(unlockBodyScroll);
}

/**
 * Checks if body scroll is currently locked.
 *
 * @returns true if at least one lock is active, false otherwise
 */
export function isBodyScrollLocked() {
  return locks.size > 0;
}
